import React, { useState, useEffect } from 'react';
import { marketingAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Megaphone, Plus, Send, Users, Eye, MousePointer, TrendingUp } from 'lucide-react';

export default function AdminMarketing() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'email', target_audience: 'all', content: '' });

  useEffect(() => { loadCampaigns(); }, []);

  const loadCampaigns = async () => {
    try {
      const res = await marketingAPI.listCampaigns();
      setCampaigns(res.data.campaigns || []);
    } catch { toast.error('Failed to load campaigns'); }
    finally { setLoading(false); }
  };

  const createCampaign = async (e) => {
    e.preventDefault();
    try {
      await marketingAPI.createCampaign(form);
      toast.success('Campaign created');
      setShowCreate(false);
      setForm({ name: '', type: 'email', target_audience: 'all', content: '' });
      loadCampaigns();
    } catch { toast.error('Failed to create campaign'); }
  };

  const statusColor = (s) => {
    const map = { draft: 'bg-gray-100 text-gray-700', active: 'bg-green-100 text-green-700', paused: 'bg-yellow-100 text-yellow-700', completed: 'bg-blue-100 text-blue-700' };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A]">Marketing & Campaigns</h1>
          <p className="text-sm text-[#5B616E] mt-1">Manage email campaigns and marketing initiatives</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#1E5A8A] hover:bg-[#1E5A8A]/90">
          <Plus className="w-4 h-4 mr-2" /> New Campaign
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Campaigns', value: campaigns.length, icon: Megaphone, color: '#1E5A8A' },
          { label: 'Active', value: campaigns.filter(c => c.status === 'active').length, icon: Send, color: '#1F7A5A' },
          { label: 'Total Reach', value: campaigns.reduce((a, c) => a + (c.metrics?.sent || 0), 0), icon: Users, color: '#B89B5E' },
          { label: 'Avg Open Rate', value: campaigns.length ? Math.round(campaigns.reduce((a, c) => a + ((c.metrics?.opened || 0) / Math.max(c.metrics?.sent || 1, 1) * 100), 0) / Math.max(campaigns.length, 1)) + '%' : '0%', icon: Eye, color: '#B3261E' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-[#E5DFD5] p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + '15', color: s.color }}>
                <s.icon className="w-4 h-4" />
              </div>
              <span className="text-xs text-[#5B616E] font-medium">{s.label}</span>
            </div>
            <p className="text-2xl font-bold text-[#1A1A1A]">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-white rounded-xl border border-[#E5DFD5] p-6">
          <h2 className="text-lg font-semibold mb-4">Create Campaign</h2>
          <form onSubmit={createCampaign} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Campaign Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" placeholder="Q1 Newsletter Campaign" />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm">
                <option value="email">Email</option>
                <option value="push">Push Notification</option>
                <option value="social">Social Media</option>
                <option value="display">Display Ads</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-[#5B616E]">Target Audience</label>
              <select value={form.target_audience} onChange={e => setForm({ ...form, target_audience: e.target.value })} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm">
                <option value="all">All Subscribers</option>
                <option value="premium">Premium Only</option>
                <option value="free">Free Users</option>
                <option value="inactive">Inactive Users</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-[#5B616E]">Content</label>
              <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={3} className="w-full mt-1 px-3 py-2 border border-[#E5DFD5] rounded-lg text-sm" placeholder="Campaign content..." />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit" className="bg-[#1E5A8A]">Create Campaign</Button>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {/* Campaign List */}
      <div className="bg-white rounded-xl border border-[#E5DFD5] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F0E8] border-b border-[#E5DFD5]">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Campaign</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Type</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Status</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Audience</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Metrics</th>
                <th className="text-left px-4 py-3 font-medium text-[#5B616E]">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5DFD5]">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5B616E]">Loading...</td></tr>
              ) : campaigns.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5B616E]">No campaigns yet. Create your first campaign.</td></tr>
              ) : campaigns.map(c => (
                <tr key={c.id} className="hover:bg-[#FBF7EF]">
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1A1A1A]">{c.name}</div>
                  </td>
                  <td className="px-4 py-3 capitalize">{c.type}</td>
                  <td className="px-4 py-3">
                    <Badge className={statusColor(c.status)}>{c.status}</Badge>
                  </td>
                  <td className="px-4 py-3 capitalize">{c.target_audience}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3 text-xs text-[#5B616E]">
                      <span className="flex items-center gap-1"><Send className="w-3 h-3" />{c.metrics?.sent || 0}</span>
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{c.metrics?.opened || 0}</span>
                      <span className="flex items-center gap-1"><MousePointer className="w-3 h-3" />{c.metrics?.clicked || 0}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#5B616E]">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
